import React, { Component, Fragment } from 'react';
import { ProductHistoryEvent } from '~/Api/Product/History';
import { formatCondensedHumanDate } from '~/DateTime';
import ActivityIndicator from '~/UI/ActivityIndicator';
import { Link } from '~/UI/Button';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import Heading from '~/UI/Heading/Heading';
import SimplePaginator from '~/UI/SimplePaginator/SimplePaginator';
import logTableStyles from '~/UI/Table/log.less';
import Table from '~/UI/Table/Table';

import styles from './styles.less';

export interface Props {
	id: string;
	events: ProductHistoryEvent[];
	showProductName: boolean;
	hasNextPage: boolean;
	isFetching: boolean;
	formatDate?(date: Date): string;
	productHistoryLink?(linK: string): string;
	onFetch?(page: string): void;
	onPrune?(id: string): void;
}

export default class HistoryTable extends Component<Props> {
	static defaultProps = {
		showProductName: false,
		formatDate: (date: Date) => formatCondensedHumanDate(date, false),
	};

	componentDidMount() {
		const { id, onFetch } = this.props;
		onFetch?.(id);
	}

	componentDidUpdate(prevProps: Props) {
		const { id, onFetch, onPrune } = this.props;

		if (prevProps.id !== id) {
			onPrune?.(id);
			onFetch?.(id);
		}
	}

	componentWillUnmount() {
		const { id, onPrune } = this.props;

		onPrune?.(id);
	}

	renderEventDescription(event: ProductHistoryEvent) {
		switch (true) {
			case event.isCompanyChange():
				return this.renderCompanyChange(event);

			case event.isPriceChange():
				return this.renderPriceChange(event);

			case event.isPresenceChange():
				return this.renderPresenceChange(event);

			default:
				return 'Unknown event';
		}
	}

	renderCompanyChange(event: ProductHistoryEvent) {
		const { companyChange } = event;
		if (companyChange === null) {
			return null;
		}

		const { previous, current } = companyChange;

		if (previous !== null && current !== null) {
			return (
				<span>
					Changed {this.renderProduct(event)} company from {this.renderCompany(previous)} to{' '}
					{this.renderCompany(current)}
				</span>
			);
		}
		if (current !== null) {
			return (
				<span>
					Set {this.renderProduct(event)} company to {this.renderCompany(current)}
				</span>
			);
		}
		if (previous !== null) {
			return (
				<span>
					Removed {this.renderProduct(event)} company {this.renderCompany(previous)}
				</span>
			);
		}

		return null;
	}

	renderCompany(company: { name: string }) {
		return <strong>{company.name}</strong>;
	}

	renderPriceChange(event: ProductHistoryEvent) {
		const { priceChange } = event;
		if (!priceChange) {
			return null;
		}

		const { previous, current } = priceChange;

		if (current && previous) {
			return (
				<span>
					Changed {this.renderProduct(event)} {current.humanFormatType()} from{' '}
					<strong>{previous.price?.humanFormat()}</strong>
					{' to '}
					<strong>{current.price?.humanFormat()}</strong>
				</span>
			);
		}
		if (current) {
			return (
				<span>
					Set {this.renderProduct(event)} {current.humanFormatType()} to <strong>{current.price?.humanFormat()}</strong>
				</span>
			);
		}
		if (previous) {
			return (
				<span>
					Removed {this.renderProduct(event)} {previous.humanFormatType()}{' '}
					<strong>{previous.price?.humanFormat()}</strong>
				</span>
			);
		}

		return null;
	}

	renderPresenceChange(event: ProductHistoryEvent) {
		const { presenceChange } = event;
		if (!presenceChange) {
			return null;
		}

		const { current } = presenceChange;
		const wasRemoved = !current || !current.presence;
		const isPresent = current && current.presence;

		if (wasRemoved) {
			return <span>{this.renderProduct(event, 'Product')} was removed from the datasource</span>;
		}
		if (isPresent) {
			return (
				<span>
					{this.renderProduct(event, 'Product')}
					{" appeared in datasource (whether this is it's first time appearing is unknown)"}
				</span>
			);
		}

		return null;
	}

	renderProduct(event: ProductHistoryEvent, genericName = 'product') {
		const { showProductName, productHistoryLink } = this.props;

		return showProductName && event.product.name && event.product.dataSource ? (
			<Fragment>
				<Link to={productHistoryLink?.(event.product.id) || ''}>{event.product.name}</Link>
				<span className={styles.countryIcon}>
					<CountryIcon
						code={event.product.dataSource.country.alpha2}
						name={event.product.dataSource.country.name}
						size={14}
					/>
				</span>
				<strong>
					{event.product.dataSource.country.alpha2} {event.product.dataSource.shortName}
				</strong>
			</Fragment>
		) : (
			genericName
		);
	}

	render() {
		const { id, events, hasNextPage, isFetching, formatDate, onFetch } = this.props;

		const eventsByYear: { [key: number]: ProductHistoryEvent[] } = {};
		const years: number[] = [];

		events.forEach((event) => {
			const year = event.effectiveSince.getFullYear();
			if (eventsByYear[year] === undefined) {
				eventsByYear[year] = [event];
				years.push(year);
			} else {
				eventsByYear[year].push(event);
			}
		});

		let counter = 0;

		return (
			<div>
				{years.map((year) => (
					<div key={year}>
						<Heading level={4}>{year}</Heading>

						<div className={styles.logTableContainer}>
							<Table styles={logTableStyles}>
								<tbody>
									{eventsByYear[year].map((event) => {
										counter += 1;

										return (
											<tr key={`${year}-${counter}`}>
												<th className={logTableStyles.headerCell}>{formatDate?.(event.effectiveSince)}</th>
												<td className={logTableStyles.cell}>{this.renderEventDescription(event)}</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</div>
					</div>
				))}

				{isFetching ? <ActivityIndicator /> : null}
				{hasNextPage && !isFetching ? <SimplePaginator onLoadMore={() => onFetch?.(id)} /> : null}
			</div>
		);
	}
}
